import { Icon } from "@norican/icons";
import type { ReactNode } from "react";
import { NavLink } from "react-router";

import { Page } from "@/components/page";
import { subText } from "@/components/table";
import { useMe } from "@/lib/api";
import { CHAPTERS } from "@/lib/help-chapters";

/**
 * The help centre's frame: chapters down the left, the chapter's own sections
 * under them, content on the right.
 *
 * One narrative page had grown past what anyone reads standing in a form, and
 * three more features had arrived since it was written. Timo, 6 Oct 2026:
 * "we need to make the whole help part more structured". So: a chapter per
 * job, each with an "on this page" list, and the same frame for all of them.
 */
export type HelpSection = { id: string; title: string };

export function HelpLayout({
  title,
  intro,
  sections = [],
  children,
}: {
  title: string;
  intro?: ReactNode;
  sections?: HelpSection[];
  children: ReactNode;
}) {
  const me = useMe().data;
  const chapters = CHAPTERS.filter((c) => !c.show || c.show(me));
  return (
    <Page title={title} intro={intro}>
      <div className="mt-6 flex flex-col gap-8 md:flex-row">
        <aside className="md:w-56 md:shrink-0">
          <nav aria-label="Help chapters" className="md:sticky md:top-4">
            <h2 className="text-fg-muted text-xs font-medium">Chapters</h2>
            <ul className="mt-2 space-y-0.5">
              {chapters.map((c) => (
                <li key={c.to}>
                  <NavLink
                    to={c.to}
                    end
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded px-2 py-1 text-sm ${
                        isActive
                          ? "text-fg bg-bg-subtle font-medium"
                          : "text-fg-muted hover:text-fg hover:bg-bg-subtle"
                      }`
                    }
                  >
                    <Icon icon={c.icon} size="sm" />
                    {c.label}
                  </NavLink>
                </li>
              ))}
            </ul>
            {sections.length ? (
              <>
                <h2 className="text-fg-muted mt-6 text-xs font-medium">On this page</h2>
                <ul className="mt-2 space-y-0.5">
                  {sections.map((s) => (
                    <li key={s.id}>
                      <a
                        href={`#${s.id}`}
                        className="text-fg-muted hover:text-fg block rounded px-2 py-0.5 text-sm"
                      >
                        {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </nav>
        </aside>
        <div className="max-w-3xl min-w-0 flex-1 pb-8">{children}</div>
      </div>
    </Page>
  );
}

/** One section of a chapter, with the anchor the side list points at. */
export function HelpSectionBlock({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-h`} className="mt-8 scroll-mt-4 space-y-2 text-sm">
      <h2 id={`${id}-h`} className="text-fg text-base font-semibold">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** A consequence worth a border, inside a section. */
export function Caution({ children }: { children: ReactNode }) {
  return <p className={`${subText} border-warning-border mt-2 border-l-2 pl-3`}>{children}</p>;
}
